import { updateModuleCache, cacheAllModuleFiles } from '@/store/sync/cachingUtils';
import { performInMemorySearch } from '@/components/search/searchStore';
import api from './api';
import moduleIndex from './index.js';
import MODULE_ID from './moduleId';
import { push } from '@/common/utils';
import { SYNC_STATES } from '@/store/sync/sync';
import dataUtils from '@/common/dataUtils';

const ROOT = { root: true };

const state = {
  duties: [],
  dutyActivities: [],
  dutyStops: [],
  dutiesById: {},
  dutyStopsById: {},
  teams: {}, // currently RhB Zug teams
  users: [], // currently used for RhB Zug teams
  usersById: [],
  // View states
  dutiesDisplayMode: null, //list or boxes
  displayedDocument: null,
  displayedDutyPart: {
    duty: null,
    dateIsoString: null,
    track: null,
    line: null,
    startToMinutesAfterMidnight: null,
    endToMinutesAfterMidnight: null,
  },
  isSplitViewCalendarVisible: false, // applies on xs and sm devices in split view, that is calendar + duty
  firsOfCalendarMonth: null, // applies in split view only
  isLoaded: false,
  displayMode: 'duty', //duty or document
};

const getters = {
  /** ------------------------------------------------------------------------------------------------------------------
   * Module Implementation for unread badge counts by instance.
   * IMPLEMENT this getter to enable unread counter badges
   * -------------------------------------------------------------------------------------------------------------------
   */
  getUnreadCountByModuleInstance: (state, getters, rootState, rootGetters) => (instance) => {
    return 0;
  },
};

const mutations = {
  setData(state, { duties, dutyActivities, dutyStops, dutiesById, dutyStopsById, teams, users, usersById }) {
    state.duties = duties;
    state.dutyActivities = dutyActivities;
    state.dutyStops = dutyStops;
    state.dutiesById = dutiesById;
    state.dutyStopsById = dutyStopsById;
    state.teams = teams;
    state.users = users;
    state.usersById = usersById;

    state.isLoaded = true;
  },
  setIsSplitViewCalendarVisible(state, value) {
    console.debug('setIsSplitViewCalendarVisible', value);
    state.isSplitViewCalendarVisible = value;
  },
  setFirsOfCalendarMonth(state, value) {
    state.firsOfCalendarMonth = value;
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for UPDATING Module counters
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  countModuleItems(state) {},
  setDisplayedDocument(state, document) {
    state.displayedDocument = document;
    state.displayMode = 'document';
  },
  setDutiesDisplayMode(state, value) {
    state.dutiesDisplayMode = value;
  },
  setDisplayMode(state, value) {
    state.displayMode = value;
  },
  setDisplayedDutyPart(state, { duty, dateIsoString, track, line, startToMinutesAfterMidnight, endToMinutesAfterMidnight }) {
    console.debug(
      `setDisplayedDutyPart dateIsoString=${dateIsoString}, track=${track}, line=${line}, startToMinutesAfterMidnight=${startToMinutesAfterMidnight}, endToMinutesAfterMidnight=${endToMinutesAfterMidnight}`
    );
    state.displayedDutyPart = {
      ...state.displayedDutyPart,
      duty,
      dateIsoString,
      track,
      line,
      startToMinutesAfterMidnight,
      endToMinutesAfterMidnight,
    };
    state.displayMode = 'duty';
  },
  resetView(state) {
    state.displayedDocument = null;
    state.displayedDutyPart = {
      duty: null,
      dateIsoString: null,
      track: null,
      line: null,
      startToMinutesAfterMidnight: null,
      endToMinutesAfterMidnight: null,
    };
    state.displayMode = 'duty';
  },
};

const actions = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for IN-MEMORY SEARCH
   * IMPLEMENT this getter to enable module in-memory search
   * Call the action in search/searchStore (search for "Module in-memory searches" there)
   * Search all documents containing all of the space separated search words in state.filterCriteria
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  searchModuleInMemoryContainingSearchTerm({ state }, { filterCriteria, options }) {
    return performInMemorySearch({
      moduleId: MODULE_ID,
      items: state.duties,
      titleSearchAttr: 'longName',
      contentSearchAttr: null,
      searchCriteriaArray: filterCriteria,
      options,
    });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Updates the browsers cache storage oevpad-api/modules/{moduleId} with the exact same data structure as received by the module sync api call!
   * Reason: This mutation MUST be called whenever data has changed, e.g. user_state data. It assures that the application cache is up-to-date
   *         when the user synchronized/updates in offline mode.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async updateModuleApplicationCache({ state }) {
    // const { duties, dutyActivities, dutyStops, dutyActivitiesMedia } = state;
    // await updateModuleCache(moduleIndex.api, { duties, dutyActivities, dutyStops, dutyActivitiesMedia });
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Dispatched in store/sync !!!
   * Gets dispatched after all sync processes have been completed.
   * This is the place to implement any post sync processes.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async onSyncComplete({ commit, dispatch, rootState, rootGetters }) {
    // Implement this method if necessary
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeFiles({ commit, dispatch, state }) {
    // no other files than fahrdienst452 files. Thus synch done within fahrdienst452 module
    // await cacheAllModuleFiles({ files: state.dutyActivitiesMedia, moduleIndex, commit, dispatch });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeData({ commit, dispatch, rootState, rootGetters }) {
    try {
      const response = await api.getData();
      let { duties, dutyActivities, dutyStops, dutyActivitiesMedia = [], teams, users } = response.data;
      const dutiesById = dataUtils.buildIdMap(duties, 'id');
      const dutyStopsById = dataUtils.buildIdMap(dutyStops, 'id');
      const usersById = dataUtils.buildIdMap(users, 'id');
      dutyActivities.forEach((da) => {
        let duty = dutiesById[da.dutyIdFk];
        if (duty) {
          push(duty, '_dutyActivities', da);
        }
      });

      duties.forEach((d) => {
        // add calculated data used mainly for displaying in live-search
        let moduleConfig = rootGetters['configs/getInstanceModuleConfig']({ moduleType: moduleIndex.moduleType });

        d._displayLocation = '';
        d._moduleTitle = moduleConfig.title;
        d._path = `/myduties/${d.id}`; //TODO: Create route for opening duty directly
        d._moduleId = MODULE_ID;

        if (d._dutyActivities) {
          d._dutyActivities.forEach((da) => {
            if (dutyStopsById[da.startStopIdFk]) da._startStop = dutyStopsById[da.startStopIdFk];
            if (dutyStopsById[da.endStopIdFk]) da._endStop = dutyStopsById[da.endStopIdFk];
          });
        }
      });
      commit('setData', { duties, dutyActivities, dutyStops, dutiesById, dutyStopsById, teams, users, usersById });
      commit('sync/setSyncState', { MODULE_ID, syncState: SYNC_STATES.COMPLETED }, ROOT);
    } catch (err) {
      console.debug(err);
      commit('sync/setSyncState', { MODULE_ID, syncState: SYNC_STATES.FAILURE, msg: err }, ROOT);
      throw err;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
